import {
  Box,
  ConnectionStatusEnum,
  Flex,
  FormControlSizes,
  Icon,
  IconButton,
  IconName,
  MarketColorDot,
  MixpanelEvent,
  Popover,
  Text,
  useDynamicCallback,
  useMarketAccountsContext,
  useMarketDataSnapshot,
  useMarketsAndMarketAccountsForMarketDataSnapshot,
  useMarketsContext,
  useMixpanel,
  useObservableValue,
  usePopoverState,
  type BuyingPower,
  type MarketDataSnapshot,
  type Security,
} from '@talos/kyoko';
import { useCallback, useMemo, type ReactNode } from 'react';
import { OverflowList } from 'react-overflow-list';
import { useTheme } from 'styled-components';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';
import { MarketTag } from '../styles';
import { MarketStatus } from './MarketStatus';
import { SelectedMarketsDots } from './SelectedMarkets';
import { MarketListWrapper, MarketsOverflowWrapper } from './styles';

interface MarketsListProps {
  security: Security;
  baseBuyingPower: Map<string, BuyingPower> | undefined;
  children?: ReactNode;
}

export function MarketsList({ security, baseBuyingPower, children }: MarketsListProps) {
  const {
    selectedMarkets,
    setSelectedMarkets,
    selectedMarketAccounts,
    setSelectedMarketAccounts,
    selectedCombined,
    setSelectedAggregation,
  } = useDeepDiveContext();
  const mixpanel = useMixpanel();

  const handleRemoveMarket = useDynamicCallback((market: string) => {
    mixpanel.track(MixpanelEvent.UpdateMarketSelection);
    setSelectedMarketAccounts(selectedMarketAccounts.filter(selectedMarket => selectedMarket !== market));
    setSelectedMarkets(selectedMarkets.filter(selectedMarket => selectedMarket !== market));
    setSelectedAggregation(undefined);
  });

  const snapshotMarkets = useMarketsAndMarketAccountsForMarketDataSnapshot(
    selectedCombined.length > 0 ? selectedCombined : (security.Markets ?? [])
  );

  // This subscription has markets connection statuses shown below
  const { marketDataSnapshots } = useMarketDataSnapshot({
    symbol: security.Symbol,
    currency: security.BaseCurrency,
    markets: snapshotMarkets,
    depth: 1,
    tag: 'DeepDive-MarketsList',
    priceIncrement: security.DefaultPriceIncrement,
    includeTradeDirection: false,
  });

  const marketData = useObservableValue(() => marketDataSnapshots, [marketDataSnapshots]);

  const ItemRenderer = useCallback(
    (market: string) => {
      return (
        <Market
          key={`market-${market}`}
          security={security}
          market={market}
          marketData={marketData}
          buyingPower={baseBuyingPower?.get(market)}
          onRemove={handleRemoveMarket}
        />
      );
    },
    [baseBuyingPower, handleRemoveMarket, marketData, security]
  );

  const OverflowRenderer = useCallback(
    (markets: string[]) => {
      return (
        <Flex flex="1" gap="spacing8">
          <MarketsOverflow
            markets={markets}
            security={security}
            marketData={marketData}
            baseBuyingPower={baseBuyingPower}
            onRemove={handleRemoveMarket}
          />
          {children}
        </Flex>
      );
    },
    [children, security, marketData, handleRemoveMarket, baseBuyingPower]
  );

  return (
    <MarketListWrapper data-testid="markets-list">
      <OverflowList
        className="markets-list"
        collapseFrom="end"
        minVisibleItems={0}
        items={selectedCombined}
        itemRenderer={ItemRenderer}
        overflowRenderer={OverflowRenderer}
        alwaysRenderOverflow={true}
      />
    </MarketListWrapper>
  );
}

function Market({
  security,
  market,
  marketData,
  buyingPower,
  onRemove,
  fromOverflow,
}: {
  security: Security;
  market: string;
  marketData?: MarketDataSnapshot;
  buyingPower: BuyingPower | undefined;
  onRemove: (market: string) => void;
  fromOverflow?: boolean;
}) {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const ma = marketAccountsByName.get(market);
  const marketName = ma?.Market ?? market;
  const m = marketDisplayNameByName.get(marketName);
  const marketDisplayName = ma?.DisplayName ?? m ?? market;

  const theme = useTheme();
  const connectionStatus = marketData?.Markets?.[marketName]?.Status as ConnectionStatusEnum;
  const hasError = connectionStatus && connectionStatus !== ConnectionStatusEnum.Online;

  const popover = usePopoverState({
    trigger: 'hover',
    placement: fromOverflow ? 'left' : 'bottom',
    usePortal: true,
  });

  return (
    <Popover {...popover}>
      <MarketTag>
        <MarketColorDot market={marketName} />

        <Text color="colors.gray.100" size={theme.fontSizeSmall}>
          {marketDisplayName}
        </Text>
        {hasError && <Icon icon={IconName.ExclamationSolid} color={theme.colors.yellow.lighten} />}
        <Flex flex="1" justifyContent="flex-end">
          <IconButton
            icon={IconName.Close}
            color="colors.gray.080"
            size={FormControlSizes.Tiny}
            onClick={() => onRemove(market)}
            ghost
          />
        </Flex>
      </MarketTag>
      <MarketStatus security={security} buyingPower={buyingPower} market={market} connectionStatus={connectionStatus} />
    </Popover>
  );
}

interface MarketsOverflowProps {
  markets: string[];
  security: Security;
  marketData?: MarketDataSnapshot;
  baseBuyingPower: Map<string, BuyingPower> | undefined;
  onRemove: (market: string) => void;
}

function MarketsOverflow({ markets, security, marketData, baseBuyingPower, onRemove }: MarketsOverflowProps) {
  const theme = useTheme();
  const { marketAccountsByName } = useMarketAccountsContext();
  const popover = usePopoverState({
    trigger: 'hover',
    placement: 'bottom',
  });

  const marketNames = useMemo(() => {
    return markets.map(market => {
      const ma = marketAccountsByName.get(market);
      return ma?.Market ?? market;
    });
  }, [markets, marketAccountsByName]);

  if (!markets.length) {
    return null;
  }

  return (
    <Flex alignItems="center" mr="spacing8">
      <Popover {...popover}>
        <MarketTag>
          <Text color="colors.gray.100" size={theme.fontSizeSmall}>
            <Flex alignItems="center">
              {markets.length} More
              <Box mr="spacing8">
                <SelectedMarketsDots marketNames={marketNames} borderColor={theme.colors.gray['010']} />
              </Box>
              <Icon icon={IconName.ChevronDown} color="colors.gray.080" />
            </Flex>
          </Text>
        </MarketTag>
        <MarketsOverflowWrapper>
          {markets.map(market => {
            const buyingPower = baseBuyingPower?.get(market);
            return (
              <Market
                key={market}
                security={security}
                market={market}
                marketData={marketData}
                buyingPower={buyingPower}
                onRemove={onRemove}
                fromOverflow={true}
              />
            );
          })}
        </MarketsOverflowWrapper>
      </Popover>
    </Flex>
  );
}
